import { NavLink, matchPath, useLocation } from 'react-router-dom';

import { AccountDashboardTypeResource } from '@datarock/api-client';
import { DOMAIN } from '../../../consts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrganizationPath } from '../../../routing';
import React, { Suspense } from 'react';
import { Spinner } from 'react-bootstrap';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { getLastElementString } from '../../utils/CommonFunction';
import { routes } from '../../../routing';
import strings from '../../../strings';
import styles from './Breadcrumbs.module.scss';
import { useAuth } from 'oidc-react';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import { useSuspense } from '@data-client/react';

interface IBreadcrumbProps {
  [key: string]: string;
  'usage&Access': string;
  installations: string;
  newAccounts: string;
  activeUsers: string;
}

interface BreadcrumbEntry {
  key: string;
  match: { url: string; path?: string };
  breadcrumb: React.ReactNode;
}

function BreadcrumbsContent() {
  const breadcrumbs = useBreadcrumbs(
    routes.map(({ breadcrumb, path }) => ({ breadcrumb, path }))
  ).slice(1);

  const { userData } = useAuth();

  const dashboardType = useSuspense(
    AccountDashboardTypeResource.get,
    userData
      ? {
          accountId: userData?.profile.sub
        }
      : null
  );

  const location = useLocation();

  const isOrganizationPath = matchPath(location.pathname, OrganizationPath);

  if (
    isOrganizationPath &&
    DOMAIN === 'admin' &&
    dashboardType?.type === 'SINGLE'
  ) {
    breadcrumbs.shift();
  }

  const breadcrumbFormatting = (breadcrumb: React.ReactNode, key: string) => {
    const breadcrumbData: IBreadcrumbProps = {
      'usage&Access': strings.pageAdminOrganizationReportUsageAccess,
      encryptsDecrypts: strings.pageAdminOrganizationReportEncrypts,
      installations: strings.pageAdminOrganizationReportInstallations,
      newAccounts: strings.pageAdminOrganizationReportNewAccounts,
      activeUsers: strings.pageAdminOrganizationReportActiveUsers
    };
    const lastPath = getLastElementString(key);
    return breadcrumbData[lastPath] ? breadcrumbData[lastPath] : breadcrumb;
  };

  return (
    <div className={styles.breadcrumbsWrapper}>
      {breadcrumbs.map(({ key, match, breadcrumb }: BreadcrumbEntry, index) => (
        <React.Fragment key={key}>
          {index > 0 ? (
            <FontAwesomeIcon
              key={`delimiter-${index}`}
              className={styles.delimiterIcon}
              icon={faAngleRight}
            />
          ) : null}

          <NavLink
            to={{
              pathname: `${match.url}${
                match.path === '/companies/:organizationId' ? '/details' : ''
              }`
            }}
            className={styles.breadcumbsRoutes}>
            <span>{breadcrumbFormatting(breadcrumb, key)}</span>
          </NavLink>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Breadcrumbs() {
  return (
    <Suspense
      fallback={
        <div
          className={styles.breadcrumbsWrapper}
          aria-busy="true"
          aria-label="Loading breadcrumbs">
          <span className={styles.breadcrumbSpinnerWrap}>
            <Spinner animation="border" role="status" />
            <span className="visually-hidden">Loading...</span>
          </span>
        </div>
      }>
      <BreadcrumbsContent />
    </Suspense>
  );
}
